/**
 * Eventi e-commerce GA4 nel dataLayer (container GTM-5FLF2JQJ, caricato in index.html).
 *
 * Ogni push è protetto da try/catch: il tracking NON deve mai rompere il sito.
 * Emettiamo eventi standard GA4 Enhanced Ecommerce (view_item, add_to_cart,
 * remove_from_cart, view_cart, begin_checkout, purchase) + un evento custom
 * contact_click. Gli eventi finiscono su window.dataLayer: arriveranno a GA4
 * solo quando il container GTM è configurato per instradarli come tag GA4.
 *
 * item_id: usiamo lo SLUG del prodotto dove disponibile (leggibile e stabile);
 * per il purchase, dove l'API ordine non espone lo slug, si usa il product_id.
 * (Non esistono SKU/varianti nel modello dati: vedi nota per il team marketing.)
 */

import { track } from "./tracker";

interface Ga4Item {
  item_id: string;
  item_name: string;
  price?: number;    // in euro
  quantity?: number;
}

export interface TrackProduct {
  item_id: string;
  item_name: string;
  price_cents: number;
}

function dataLayer(): unknown[] {
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

// Consenso cookie (stesso storage di CookieConsent.tsx): finché l'utente non
// clicca "Accetta" NON spingiamo alcun evento e-commerce nel dataLayer → GDPR.
const CONSENT_KEY = "gb_cookie_consent"; // "granted" | "denied"
function consentGranted(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

function euros(cents: number): number {
  return Math.round(cents || 0) / 100;
}

/** Traduce l'oggetto `ecommerce` di GA4 nelle proprietà del collector proprietario.
 *  Il collector accetta solo valori semplici: l'array `items` viene ridotto al
 *  primo elemento (quello su cui l'utente ha agito) più il conteggio. */
function toTrackerProps(ecommerce: Record<string, unknown>): Record<string, string | number | undefined> {
  const items = Array.isArray(ecommerce.items) ? (ecommerce.items as Ga4Item[]) : [];
  const first = items[0];
  return {
    item_id: first?.item_id,
    item_name: first?.item_name,
    quantity: first?.quantity,
    items_count: items.length || undefined,
    // Sul purchase il numero d'ordine è il progressivo interno, non un dato
    // personale: serve a riconciliare gli acquisti con la tabella ordini.
    order_number: typeof ecommerce.transaction_id === "string" ? ecommerce.transaction_id : undefined,
    tax_cents: typeof ecommerce.tax === "number" ? Math.round(ecommerce.tax * 100) : undefined,
    coupon_code: typeof ecommerce.coupon === "string" ? ecommerce.coupon : undefined,
  };
}

function push(event: string, ecommerce: Record<string, unknown>): void {
  try {
    if (!consentGranted()) return; // nessun tracciamento prima del consenso
    const layer = dataLayer();
    // Reset dell'oggetto ecommerce tra un evento e l'altro (best practice GA4:
    // evita che gli item di un evento si "mescolino" col successivo).
    layer.push({ ecommerce: null });
    layer.push({ event, ecommerce });
  } catch {
    /* il tracking non deve mai interrompere il flusso di acquisto */
  }
  // Secondo canale, indipendente da Google: stesso evento verso il nostro
  // collector. Fase di doppio binario — GA4 resta attivo finché i numeri dei
  // due sistemi non coincidono (vedi GB-BACKEND/docs/analytics-audit.md).
  try {
    const value = typeof ecommerce.value === "number" ? ecommerce.value : undefined;
    track(event, toTrackerProps(ecommerce), {
      valueCents: value !== undefined ? Math.round(value * 100) : undefined,
      currency: typeof ecommerce.currency === "string" ? ecommerce.currency : undefined,
    });
  } catch {
    /* idem: mai propagare errori di tracciamento */
  }
}

function toItem(p: TrackProduct, quantity: number): Ga4Item {
  return { item_id: p.item_id, item_name: p.item_name, price: euros(p.price_cents), quantity };
}

/**
 * `view_item` soppresso perché il consenso non era ancora stato dato. La scheda
 * prodotto emette l'evento al mount: chi apre il sito, arriva su un prodotto e
 * accetta i cookie lì perderebbe per sempre la visualizzazione di QUELLA pagina.
 * Viene conservato l'ultimo, con il path in cui è stato catturato, e riemesso da
 * `flushAfterConsent()` solo se l'utente è ancora sulla stessa pagina.
 */
let pendingViewItem: { p: TrackProduct; currency: string; path: string } | null = null;

export interface PurchaseArgs {
  transaction_id: string;
  valueCents: number;
  currency: string;
  items: Ga4Item[];
  /** IVA in centesimi, così com'è sull'ordine: MAI ricalcolata qui. */
  taxCents?: number | null;
  coupon?: string | null;
  /**
   * Chiave anti-duplicato in localStorage, scritta SOLO quando l'evento parte
   * davvero. Prima veniva scritta anche senza consenso: l'acquisto restava
   * segnato come «già inviato» e non arrivava più, nemmeno dopo l'accettazione (R-74).
   */
  dedupeKey?: string;
}

/** `purchase` soppresso in attesa del consenso, inviato da `flushAfterConsent()`. */
let pendingPurchase: PurchaseArgs | null = null;
/** Anti-duplicato anche quando localStorage non è disponibile (navigazione privata). */
const sentThisSession = new Set<string>();

function alreadySent(key: string | undefined): boolean {
  if (!key) return false;
  if (sentThisSession.has(key)) return true;
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export const ga4 = {
  viewItem(p: TrackProduct, currency = "EUR"): void {
    if (!consentGranted()) {
      try {
        pendingViewItem = { p, currency, path: window.location.pathname };
      } catch {
        /* noop */
      }
      return;
    }
    push("view_item", { currency, value: euros(p.price_cents), items: [toItem(p, 1)] });
  },
  /** Chiamata da CookieConsent subito dopo l'accettazione. */
  flushAfterConsent(): void {
    const purchase = pendingPurchase;
    pendingPurchase = null;
    if (purchase) {
      // A differenza della visualizzazione, l'acquisto è un fatto avvenuto in
      // questa sessione: vale anche se la pagina è cambiata. Una volta sola (dedupeKey).
      ga4.purchase(purchase);
    }
    const pending = pendingViewItem;
    pendingViewItem = null;
    if (!pending) return;
    try {
      if (window.location.pathname !== pending.path) return; // pagina cambiata: sarebbe un dato falso
    } catch {
      return;
    }
    ga4.viewItem(pending.p, pending.currency);
  },
  addToCart(p: TrackProduct, quantity: number, currency = "EUR"): void {
    push("add_to_cart", { currency, value: euros(p.price_cents * quantity), items: [toItem(p, quantity)] });
  },
  removeFromCart(p: TrackProduct, quantity: number, currency = "EUR"): void {
    push("remove_from_cart", { currency, value: euros(p.price_cents * quantity), items: [toItem(p, quantity)] });
  },
  viewCart(items: Ga4Item[], valueCents: number, currency = "EUR"): void {
    push("view_cart", { currency, value: euros(valueCents), items });
  },
  beginCheckout(items: Ga4Item[], valueCents: number, currency = "EUR"): void {
    push("begin_checkout", { currency, value: euros(valueCents), items });
  },
  /** Vero se l'evento è partito; senza consenso resta in attesa e torna falso. */
  purchase(args: PurchaseArgs): boolean {
    if (alreadySent(args.dedupeKey)) return false;
    if (!consentGranted()) {
      pendingPurchase = args;
      return false;
    }
    const ecommerce: Record<string, unknown> = {
      transaction_id: args.transaction_id,
      value: euros(args.valueCents),
      currency: args.currency || "EUR",
      items: args.items,
    };
    // Campi opzionali solo se realmente presenti: niente parametri a `undefined`.
    // `shipping` non viene inviato: l'API dell'ordine non espone un importo di
    // spedizione separato (è incluso nel prezzo), quindi sarebbe un dato inventato.
    if (args.taxCents != null) ecommerce.tax = euros(args.taxCents);
    if (args.coupon) ecommerce.coupon = args.coupon;
    push("purchase", ecommerce);
    if (args.dedupeKey) {
      sentThisSession.add(args.dedupeKey);
      try {
        localStorage.setItem(args.dedupeKey, "1");
      } catch {
        /* storage non disponibile: al massimo un doppione, mai una perdita */
      }
    }
    return true;
  },
  contactClick(method: "phone" | "email" | "chat", location = "cart"): void {
    try {
      if (!consentGranted()) return; // nessun tracciamento prima del consenso
      dataLayer().push({ event: "contact_click", contact_method: method, contact_location: location });
      track("contact_click", { contact_method: method, contact_location: location });
    } catch {
      /* noop */
    }
  },
  /** Invio riuscito di un form di contatto (lead). `form_name` distingue le origini. */
  formSubmit(formName: "contact_page" | "send_request_aside", location = "site"): void {
    try {
      if (!consentGranted()) return; // nessun tracciamento prima del consenso
      dataLayer().push({ event: "form_submit", form_name: formName, form_location: location });
      track("form_submit", { form_name: formName, form_location: location });
    } catch {
      /* noop */
    }
  },
};
